// @flow
// $FlowFixMe
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { get, attempt, isError } from "lodash";
import { connect, useDispatch } from "react-redux";
import { setPrevPath } from "@fdr/shared-actions/AppActions";
import type { Dispatch } from "redux";
import type { HeaderProps, RaceListItem } from "@fdr/types/ProgramPage";
import { type RaceProgram } from "@tvg/types/Race";
import mediator from "@tvg/mediator";
import RaceVideo from "@fdr/video";
import tvgConf from "@tvg/conf";
import {
  useNextRaceUrl,
  usePreviousRaceUrl
} from "@fdr/custom-hooks/ProgramPage";
import buildRaceUrl, { isFullURL } from "@tvg/formatter/url";
import { showVideo } from "@fdr/shared-actions/video";
import RaceSelector from "@fdr/atomic-ui/_organisms/RaceSelector";
import getOptedInPromos from "@fdr/utils/PromoUtils";
import { useAutoPlayPPVideos } from "@urp/sh-lib-amplitude-experiments";
import {
  FREE_PICK_SOURCE,
  getFreePickSource,
  isSRW
} from "@fdr/utils/trackDataSourceUtils";
import { openExternalLink } from "@tvg/sh-utils/mobileUtils";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import { parseJSONCapiMessage } from "@fdr/utils/parseJSONCapiMessage";
import { DefaultMTPText } from "@fdr/atomic-ui/_atoms/MTP";
import { DefaultPromoText } from "@fdr/atomic-ui/_molecules/PromoButton";
import {
  getHomepageCurrentConfig,
  getHomepageEventInfo,
  getTodayDate
} from "@fdr/home/src/redux/selectors";
import { getHomepageDerbyBanner } from "@fdr/home-header/store/selectors";
import { getEventType } from "@fdr/home/src/utils/getEventType";
import { events as AlchemerEvents } from "@urp/alchemer";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import { getAccountNumber } from "@urp/store-selectors";
import { RaceVideoFeedback } from "@tvg/ts-types/Race";

export const getPromoUrl = ({
  promoPath,
  oldPromosURLs
}: {
  promoPath: string,
  oldPromosURLs: string[]
}): string => {
  let promoPathResult = "";
  if (promoPath) {
    promoPathResult = promoPath;

    const isExternal = isFullURL(promoPath);
    if (isExternal) {
      const urlObject = new URL(promoPath);
      const isOldPromotion = oldPromosURLs.some((oldPromoURL) =>
        oldPromoURL.includes(urlObject.pathname)
      );

      if (!isOldPromotion) {
        // full URL is not included in old promos, must be parsed
        promoPathResult = urlObject.pathname;
      }
    } else {
      const isOldPromotion = oldPromosURLs.some((oldPromoURL) =>
        oldPromoURL.includes(promoPath)
      );

      if (isOldPromotion) {
        // not a full URL but it's an old promo, must be changed to full url
        promoPathResult = `${window.location.origin + promoPath}`;
      }
    }
  }

  return promoPathResult;
};

export const sendGTMEvent = (
  type: string,
  props: HeaderProps,
  derbyProps?: {
    isDerbyRace?: boolean,
    backButtonLink?: string,
    nextButtonLink?: string,
    derbyTitle?: string
  }
) => {
  const trackName = get(props, "race.track.trackName");
  const raceNumber = get(props, "race.raceNumber");
  const raceMtp = get(props, "race.mtp");
  const raceInfo = {
    ...get(props, "race"),
    promo: getOptedInPromos(
      get(props, "race.promos", []),
      props.optedInPromos,
      props.isUserLogged
    )
  };
  const isDerbyRace = get(derbyProps, "isDerbyRace", false);
  switch (type) {
    case "NEXT_RACE":
      mediator.base.dispatch({
        type: "PPM_NEXT_RACE",
        payload: {
          trackName,
          raceNumber: +raceNumber + 1
        }
      });
      if (isDerbyRace) {
        const linkUrl = get(derbyProps, "nextButtonLink");
        const bannerName = get(derbyProps, "derbyTitle");

        mediator.base.dispatch({
          type: "DERBY_HEADER_SELECTED_RACE",
          payload: {
            linkUrl,
            bannerName
          }
        });
      }
      break;
    case "PREVIOUS_RACE":
      mediator.base.dispatch({
        type: "PPM_PREVIOUS_RACE",
        payload: {
          trackName,
          raceNumber: +raceNumber - 1
        }
      });
      if (isDerbyRace) {
        const linkUrl = get(derbyProps, "backButtonLink");
        const bannerName = get(derbyProps, "derbyTitle");

        mediator.base.dispatch({
          type: "DERBY_HEADER_SELECTED_RACE",
          payload: {
            linkUrl,
            bannerName
          }
        });
      }
      break;
    case "OPEN_TRACK_MODAL":
      mediator.base.dispatch({
        type: "PPM_OPEN_UPCOMING",
        payload: {
          trackName,
          raceNumber
        }
      });
      break;
    case "PROMOS":
      mediator.base.dispatch({
        type: "PPM_PROMOS",
        payload: {
          trackName,
          raceNumber,
          optedIn: !!get(raceInfo, "promo.isOptedIn", false)
        }
      });
      break;
    case "RACING_TIP_MORE":
      mediator.base.dispatch({
        type: "PPM_RACING_TIP_MORE",
        payload: {
          trackName,
          raceNumber,
          mtp: raceMtp
        }
      });
      break;
    default:
  }
};

interface IOnVideoParams {
  dispatch: Dispatch<any>;
  isUserLogged: boolean;
  isAccountCompliant: boolean | typeof undefined;
  videoFeedback: string;
  method?: string;
}

export const onVideoClick = (props: IOnVideoParams) => {
  const { dispatch, isUserLogged, isAccountCompliant, videoFeedback } = props;
  if (!isUserLogged) {
    mediator.base.dispatch({
      type: "OPEN_LOGIN",
      payload: {
        callback: () => {
          dispatch(showVideo(props?.method));
          videoFeedback.includes("AVAILABLE_NO_REPLAY") &&
            AlchemerEvents.selectWatchLiveButton();
        }
      }
    });
    return;
  }

  if (!isAccountCompliant) {
    onOpenComplianceModal();
    return;
  }

  dispatch(showVideo(props?.method));
  videoFeedback.includes("AVAILABLE_NO_REPLAY") &&
    AlchemerEvents.selectWatchLiveButton();
};

export const getRaceData = (
  racesData: Array<?(RaceListItem[])>,
  races: RaceProgram[],
  race: RaceProgram
) =>
  racesData &&
  racesData.length > 0 &&
  racesData[
    races.findIndex((raceItem) => raceItem.raceNumber === race.raceNumber)
  ];

export const Header = (props: HeaderProps) => {
  const {
    racesData,
    optedInPromos,
    races,
    race,
    videoFeedback,
    channel,
    bettingInterests,
    loadingRunners,
    isUserLogged,
    isWagerable,
    video,
    timeformForUSGSA,
    oldPromosURLs,
    MTPText,
    promoText,
    trackAbbr,
    geolocationError,
    isAccountCompliant,
    derbyInfo,
    todayDate,
    enableFAQRaceHeader,
    prevPathStore,
    derbyBannerCfg,
    eventStatus
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [finalPrevPath, setFinalPrevPath] = useState("/");
  const eventDate = get(derbyInfo, "eventDate");
  const derbyRaceTrackAbbr = get(derbyInfo, "trackAbbr");
  const derbyRaceTrackName = get(derbyInfo, "trackName");
  const derbyRaceNumber = get(derbyInfo, "raceNumber");
  const eventType = getEventType(todayDate, eventDate);
  const derbyPath = buildRaceUrl(
    derbyRaceTrackAbbr,
    derbyRaceTrackName,
    derbyRaceNumber
  );

  const bannerCfg = get(derbyBannerCfg, `${eventType}`, []).find((cfg) =>
    cfg.context.includes("programPage")
  );

  const memoizedPath = useMemo(() => {
    if (props.prevPath && props.prevPath !== "/tutorials") {
      dispatch(setPrevPath(props.prevPath));
    }
    return props.prevPath;
  }, []);

  const [raceAnalystVerdictExpanded, setRaceAnalystVerdictExpanded] =
    useState(false);

  const { nextRace, forwardDisabled, nextButtonLink } = useNextRaceUrl(
    races,
    race
  );

  const { previousRace, backDisabled, backButtonLink } = usePreviousRaceUrl(
    races,
    race
  );

  const videoShow = get(video, "show");

  const stream = get(race, "video.streams[0]", "");
  const processedRace = {
    ...race,
    promo: getOptedInPromos(
      get(race, "promos", []),
      optedInPromos,
      isUserLogged
    ),
    timeformAnalystVerdict: get(race, "timeform.analystVerdict", "")
  };
  const trackDataSource = get(race, "track.trackDataSource", "");
  const freePickSource = timeformForUSGSA
    ? getFreePickSource(trackDataSource)
    : FREE_PICK_SOURCE.DEFAULT;

  let oldPromos = oldPromosURLs || [];
  if (isError(oldPromosURLs)) {
    oldPromos = [];
  }

  const promoPathParsed = getPromoUrl({
    promoPath: get(processedRace, "promo.promoPath", ""),
    oldPromosURLs: oldPromos
  });

  const hasDerbyBanner = bannerCfg && trackAbbr === derbyRaceTrackAbbr;
  const isDerbyDay =
    eventType === "eventDay" && trackAbbr === derbyRaceTrackAbbr;
  const derbyRace = get(races, derbyRaceNumber - 1, null);
  const availableDerbyRace =
    isDerbyDay && get(derbyRace, "status.code", "RO") !== "RO"
      ? derbyRace
      : null;
  const derbyTitle = get(derbyInfo, "eventName");

  useEffect(() => {
    if (hasDerbyBanner) {
      mediator.base.dispatch({
        type: "DERBY_HEADER_VIEWED",
        payload: {
          bannerName: get(derbyTitle, "label")
        }
      });
    }
  }, [hasDerbyBanner]);

  useEffect(() => {
    setFinalPrevPath(prevPathStore);
  }, [memoizedPath, prevPathStore]);

  const videoConfig = useAutoPlayPPVideos();

  const raceMtpBelowThreshold =
    race && race.mtp !== undefined
      ? race.mtp <= videoConfig.mtpThreshold
      : false;

  useEffect(() => {
    const shouldAutoPlay =
      videoConfig.autoPlay &&
      raceMtpBelowThreshold &&
      videoFeedback === RaceVideoFeedback.AVAILABLE_NO_REPLAY;

    if (shouldAutoPlay) {
      onVideoClick({
        dispatch,
        isUserLogged,
        isAccountCompliant,
        videoFeedback,
        method: "auto_play"
      });
    }
  }, [
    videoConfig.autoPlay,
    dispatch,
    isUserLogged,
    isAccountCompliant,
    videoFeedback,
    raceMtpBelowThreshold
  ]);

  const onBannerCtaClick = (linkText: string, url: string) => {
    const bannerName = get(derbyTitle, "label");
    const linkUrl = url.includes("#derbyRace") ? derbyPath : url;

    mediator.base.dispatch({
      type: "DERBY_HEADER_CLICK",
      payload: {
        linkUrl,
        bannerName,
        linkText
      }
    });

    navigate(linkUrl);
  };

  return useMemo(
    () =>
      videoShow ? (
        <RaceVideo
          currentRace={race}
          stream={stream}
          channel={channel}
          feedback={videoFeedback}
          trackAbbr={trackAbbr}
        />
      ) : (
        <RaceSelector
          prevPath={finalPrevPath}
          race={processedRace}
          derbyRace={availableDerbyRace}
          isWagerable={isWagerable}
          onBackPageLink={backButtonLink}
          onForwardPageLink={nextButtonLink}
          isBackDisabled={backDisabled}
          isForwardDisabled={forwardDisabled}
          trackSelectorUrl={`?race=${get(
            props,
            "race.raceNumber"
          )}#race-selector`}
          onEventClick={(event) => {
            if (event === "GO_BACK") {
              AlchemerEvents.backTapped();
            }
            if (!finalPrevPath && event === "GO_BACK") {
              navigate("/home");
            } else if (
              promoPathParsed &&
              isFullURL(promoPathParsed) &&
              tvgConf().product === "ios2"
            ) {
              event.stopPropagation();
              event.preventDefault();
              openExternalLink(promoPathParsed);
            }
            sendGTMEvent(event, props, {
              backButtonLink,
              nextButtonLink,
              isDerbyRace: !!availableDerbyRace,
              derbyTitle: get(derbyTitle, "label")
            });
          }}
          raceInfoData={getRaceData(racesData, races, race)}
          videoFeedBack={videoFeedback}
          onVideoClick={() =>
            onVideoClick({
              dispatch,
              isUserLogged,
              isAccountCompliant,
              videoFeedback
            })
          }
          bettingInterests={bettingInterests}
          loadingRunners={loadingRunners}
          raceAnalystVerdictExpanded={raceAnalystVerdictExpanded}
          setRaceAnalystVerdictExpanded={setRaceAnalystVerdictExpanded}
          isSRW={isSRW(trackDataSource)}
          freePickSource={freePickSource}
          racePromoPathParsed={promoPathParsed}
          promoText={promoText}
          MTPText={MTPText}
          isNightMode={hasDerbyBanner}
          hasDerbyBanner={hasDerbyBanner}
          hasFAQ={enableFAQRaceHeader}
          eventStatus={eventStatus}
          bannerCfg={bannerCfg}
          onBannerCtaClick={onBannerCtaClick}
        />
      ),
    [
      nextRace,
      isWagerable,
      isAccountCompliant,
      previousRace,
      loadingRunners,
      raceAnalystVerdictExpanded,
      JSON.stringify(geolocationError),
      JSON.stringify(race),
      JSON.stringify(optedInPromos),
      JSON.stringify(video),
      JSON.stringify(bettingInterests),
      hasDerbyBanner,
      derbyRace,
      prevPathStore,
      backDisabled,
      forwardDisabled,
      backButtonLink,
      nextButtonLink
    ]
  );
};

Header.defaultProps = {
  isRaceNavigationShown: false,
  isUserLogged: false,
  optedInPromos: {},
  wagerProfile: "",
  numberOfRaces: 0,
  race: null,
  channel: false,
  bettingInterests: [],
  loadingRunners: true,
  isLoading: true,
  racesData: [],
  isWagerable: true,
  accountNumber: 0,
  timeformForUSGSA: false,
  oldPromosURLs: [],
  MTPText: DefaultMTPText,
  promoText: DefaultPromoText,
  trackAbbr: "",
  prevPath: ""
};

// $FlowFixMe
export default connect((store) => ({
  accountNumber: getAccountNumber(store),
  isUserLogged: get(store, "userData.logged", false),
  isAccountCompliant: isAccountCompliantSelector(store),
  optedInPromos: get(store, "userData.optedInPromos", {}),
  video: store.RaceProgramVideo,
  bettingInterests: get(store, "RaceProgram.currentRace.bettingInterests", []),
  loadingRunners: get(store, "RaceProgram.loadingRunners", true),
  timeformForUSGSA: get(store, "capi.featureToggles.timeformForUSGSA", false),
  oldPromosURLs: attempt(() =>
    JSON.parse(get(store, "capi.messages.oldPromosURLsFDR", "[]"))
  ),
  MTPText: parseJSONCapiMessage(store, "capi.messages.MTP", DefaultMTPText),
  promoText: parseJSONCapiMessage(
    store,
    "capi.messages.promoRaceButton",
    DefaultPromoText
  ),
  geolocationError: get(store, "geolocation.geolocationError", false),
  derbyInfo: getHomepageEventInfo(store),
  derbyBannerCfg: getHomepageDerbyBanner(store),
  eventStatus: getHomepageCurrentConfig(store),
  todayDate: getTodayDate(store),
  enableFAQRaceHeader: get(
    store,
    "capi.featureToggles.enableFAQRaceHeader",
    false
  ),
  prevPathStore: get(store, "app.prevPath", "/")
}))(Header);
